import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../services/api";
import "../style.css";

function HtmlToolbar({ onInsert }) {
  const btn = (label, text) => (
    <button
      type="button"
      className="btn btn-ghost"
      onClick={() => onInsert(text)}
      style={{ padding: "8px 10px", borderRadius: 10 }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {btn("H2", "<h2>Your heading</h2>\n")}
      {btn("H3", "<h3>Your heading</h3>\n")}
      {btn("P", "<p>Your paragraph...</p>\n")}
      {btn("Bold", "<b>bold text</b>")}
      {btn("Italic", "<i>italic text</i>")}
      {btn("UL", "<ul>\n  <li>Item</li>\n</ul>\n")}
      {btn("OL", "<ol>\n  <li>Item</li>\n</ol>\n")}
      {btn("Link", '<a href="https://example.com">Link text</a>')}
      {btn("HR", "<hr/>\n")}
      {btn("Quote", "<blockquote>Your quote</blockquote>\n")}
    </div>
  );
}

export default function BlogForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = !!id;

  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // core
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [postedDate, setPostedDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("draft");
  const [excerpt, setExcerpt] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [tags, setTags] = useState("");
  const [banner, setBanner] = useState(null);
  const [existingBannerUrl, setExistingBannerUrl] = useState("");
  const [existingOgUrl, setExistingOgUrl] = useState("");


  // SEO
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDesc, setSeoDesc] = useState("");
  const [focusKeyword, setFocusKeyword] = useState("");
  const [metaRobots, setMetaRobots] = useState("index,follow");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [schemaJson, setSchemaJson] = useState("");

  // OG
  const [ogEnabled, setOgEnabled] = useState(true);
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogType, setOgType] = useState("article");
  const [ogImage, setOgImage] = useState(null);

  const previewHtml = useMemo(() => contentHtml || "<p class='muted'>Preview will show here...</p>", [contentHtml]);

  useEffect(() => {
    async function init() {
      const c = await api.get("/categories");
      const list = c.data.data || [];
      setCats(list);
      if (!categoryId && list[0]?._id) setCategoryId(list[0]._id);

      if (editing) {
        // Temporary edit-load: get list then slug, then fetch full
        const res = await api.get(`/blogs?limit=200`);
        const found = (res.data.data || []).find((x) => x._id === id);

        if (!found) {
          setErr("Blog not found (increase limit or add backend admin get-by-id).");
          return;
        }

        setTitle(found.title || "");
        setSlug(found.slug || "");
        setCategoryId(found.category?._id || "");
        setPostedDate(new Date(found.postedDate).toISOString().slice(0, 10));
        setStatus(found.status || "draft");
        setExcerpt(found.excerpt || "");

        const full = await api.get(`/blogs/${found.slug}`);
        const b = full.data;
        setExistingBannerUrl(b.bannerImage ? `http://localhost:3001${b.bannerImage}` : "");
        setExistingOgUrl(b.og?.image ? `http://localhost:3001${b.og.image}` : "");


        setContentHtml(b.contentHtml || "");
        setTags((b.tags || []).join(", "));

        setSeoTitle(b.seo?.title || "");
        setSeoDesc(b.seo?.metaDescription || "");
        setFocusKeyword(b.seo?.focusKeyword || "");
        setMetaRobots(b.seo?.metaRobots || "index,follow");
        setCanonicalUrl(b.seo?.canonicalUrl || "");
        setSchemaJson(b.seo?.schemaJson || "");

        setOgEnabled(b.og?.enabled ?? true);
        setOgTitle(b.og?.title || "");
        setOgDescription(b.og?.description || "");
        setOgType(b.og?.type || "article");
      }
    }

    init().catch(() => { });
    // eslint-disable-next-line
  }, [editing, id]);

  function insertAtCursor(text) {
    // simple append (works fine for starter)
    setContentHtml((prev) => prev + text);
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const form = new FormData();
      form.append("title", title);
      form.append("slug", slug || title);
      form.append("categoryId", categoryId);
      form.append("postedDate", postedDate);
      form.append("status", status);
      form.append("excerpt", excerpt);
      form.append("contentHtml", contentHtml);

      const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
      form.append("tags", JSON.stringify(tagArr));

      form.append("seo", JSON.stringify({
        title: seoTitle,
        metaDescription: seoDesc,
        focusKeyword,
        metaRobots,
        canonicalUrl,
        schemaJson
      }));

      form.append("og", JSON.stringify({
        enabled: ogEnabled,
        title: ogTitle,
        description: ogDescription,
        type: ogType
      }));

      if (banner) form.append("banner", banner);
      if (ogImage) form.append("ogImage", ogImage);

      if (editing) {
        await api.put(`/blogs/${id}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post(`/blogs`, form, { headers: { "Content-Type": "multipart/form-data" } });
      }

      nav("/blogs");
    } catch (ex) {
      setErr(ex?.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2>{editing ? "Edit Blog" : "New Blog"}</h2>
        <button className="btn btn-ghost" onClick={() => nav("/blogs")}>Back</button>
      </div>

      <div className="card" style={{ marginTop: 12, padding: 16 }}>
        <form onSubmit={submit} className="grid">
          <div className="row">
            <div className="field">
              <label>Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>

            <div className="field">
              <label>Slug</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto-from-title" />
            </div>
          </div>

          <div className="row-3">
            <div className="field">
              <label>Category</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                {cats.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Posted Date</label>
              <input type="date" value={postedDate} onChange={(e) => setPostedDate(e.target.value)} />
            </div>

            <div className="field">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label>Banner Image</label>

              {existingBannerUrl && !banner ? (
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <img
                    src={existingBannerUrl}
                    alt="banner"
                    style={{ width: 120, height: 70, objectFit: "cover", borderRadius: 10, border: "1px solid var(--border)" }}
                  />
                  <div className="muted" style={{ fontSize: 13 }}>
                    Current banner<br />
                    <span style={{ wordBreak: "break-all" }}>{existingBannerUrl.split("/").pop()}</span>
                  </div>
                </div>
              ) : null}

              {banner ? (
                <div className="muted" style={{ fontSize: 13 }}>
                  Selected: <b>{banner.name}</b>
                </div>
              ) : null}

              <input
                type="file"
                accept="image/*"
                onChange={(e) => setBanner(e.target.files?.[0] || null)}
              />
            </div>


            {/* <div className="field">
              <label>Tags (comma separated)</label>
              <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="audio, earbuds, guide" />
            </div> */}
          </div>

          <div className="field">
            <label>Excerpt</label>
            <textarea rows="3" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} />
          </div>

          <div className="field">
            <label>Content (HTML Editor)</label>
            <HtmlToolbar onInsert={insertAtCursor} />
            <textarea
              rows="10"
              value={contentHtml}
              onChange={(e) => setContentHtml(e.target.value)}
              placeholder="<p>Write your blog content in HTML...</p>"
            />
            <div className="muted" style={{ fontSize: 13 }}>Preview:</div>
            <div
              className="card"
              style={{ padding: 14, borderRadius: 14, background: "#fff" }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>

          <hr className="sep" />

          <h2>SEO</h2>
          <div className="row">
            <div className="field">
              <label>SEO Title</label>
              <input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} />
            </div>

            <div className="field">
              <label>Focus Keyword</label>
              <input value={focusKeyword} onChange={(e) => setFocusKeyword(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label>Meta Description</label>
            <textarea rows="3" value={seoDesc} onChange={(e) => setSeoDesc(e.target.value)} />
          </div>

          <div className="row">
            <div className="field">
              <label>Meta Robots</label>
              <input value={metaRobots} onChange={(e) => setMetaRobots(e.target.value)} />
            </div>

            <div className="field">
              <label>Canonical URL</label>
              <input value={canonicalUrl} onChange={(e) => setCanonicalUrl(e.target.value)} placeholder="https://site.com/blog/slug" />
            </div>
          </div>

          <div className="field">
            <label>Schema JSON-LD (optional)</label>
            <textarea rows="5" value={schemaJson} onChange={(e) => setSchemaJson(e.target.value)} placeholder='{"@context":"https://schema.org"}' />
          </div>

          <hr className="sep" />

          <h2>Open Graph (OG)</h2>
          <div className="row">
            <div className="field">
              <label>OG Enabled</label>
              <select value={String(ogEnabled)} onChange={(e) => setOgEnabled(e.target.value === "true")}>
                <option value="true">ON</option>
                <option value="false">OFF</option>
              </select>
            </div>

            <div className="field">
              <label>OG Type</label>
              <input value={ogType} onChange={(e) => setOgType(e.target.value)} />
            </div>
          </div>

          <div className="row">
            <div className="field">
              <label>OG Title</label>
              <input value={ogTitle} onChange={(e) => setOgTitle(e.target.value)} />
            </div>

            <div className="field">
              <label>OG Description</label>
              <input value={ogDescription} onChange={(e) => setOgDescription(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label>OG Image (optional)</label>
            <input type="file" accept="image/*" onChange={(e) => setOgImage(e.target.files?.[0] || null)} />
          </div>

          {err ? <div style={{ color: "#dc2626", fontWeight: 900 }}>{err}</div> : null}

          <button className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : (editing ? "Update Blog" : "Create Blog")}
          </button>
        </form>
      </div>
    </Layout>
  );
}
